import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import ComposerPanel from "@/components/ComposerPanel";
import ArticleList from "@/components/ArticleList";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, pending: 0, approved: 0, published: 0, scheduled: 0 });
  const [linkedinUrl, setLinkedinUrl] = useState("https://www.linkedin.com/company/wisewit-solutions/");
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [filter, setFilter] = useState("all");

  const loadAll = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/articles"),
        api.get("/stats"),
      ]);
      setArticles(a.data);
      setStats(s.data);
      if (s.data?.linkedin_url) setLinkedinUrl(s.data.linkedin_url);
    } catch {
      toast.error("Failed to load articles");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openComposer = (article = null) => {
    setEditingArticle(article);
    setComposerOpen(true);
  };

  const onSaved = async () => {
    setComposerOpen(false);
    setEditingArticle(null);
    await loadAll();
  };

  const filtered =
    filter === "all" ? articles : articles.filter((a) => a.status === filter);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav linkedinUrl={linkedinUrl} />

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        {/* Headline + KPIs */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="label-mono text-safety">// articles</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
              Hello, {user?.name?.split(" ")[0] || "Admin"}.
            </h1>
            <p className="mt-3 text-bone/60 max-w-xl">
              The orange Lingent icon is draggable — grab it, drop it anywhere, and start
              your next LinkedIn story. Or click <span className="text-bone">New Article</span> below.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() => openComposer(null)}
                data-testid="new-article-btn"
                className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11 px-6"
              >
                <Plus className="w-4 h-4 mr-2" /> New Article
              </Button>
              <Button
                variant="outline"
                onClick={loadAll}
                data-testid="refresh-articles-btn"
                className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-11"
              >
                Refresh
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-hairline border border-hairline">
            <KPI label="Total" v={stats.total} testid="kpi-total" />
            <KPI label="Pending" v={stats.pending} accent testid="kpi-pending" />
            <KPI label="Published" v={stats.published} testid="kpi-published" />
            <KPI label="Drafts" v={stats.draft} testid="kpi-draft" />
            <KPI label="Approved" v={stats.approved} testid="kpi-approved" />
            <KPI label="Scheduled" v={stats.scheduled} testid="kpi-scheduled" />
          </div>
        </div>

        {/* Filters */}
        <div className="mt-12 flex items-center gap-2 flex-wrap border-b border-hairline pb-3">
          {["all", "draft", "pending", "approved", "published", "scheduled"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}-btn`}
              className={`label-mono px-3 py-1.5 border ${
                filter === s
                  ? "bg-bone text-ink border-bone"
                  : "border-hairline text-bone/70 hover:text-bone"
              }`}
            >
              {s} {s !== "all" && stats[s] !== undefined ? `(${stats[s]})` : ""}
            </button>
          ))}
        </div>

        {/* Articles */}
        <div className="mt-8">
          <ArticleList
            articles={filtered}
            onEdit={openComposer}
            onChanged={loadAll}
            linkedinUrl={linkedinUrl}
          />
        </div>
      </main>

      {/* Draggable Lingent floating icon */}
      <LingentFloatingIcon onOpen={() => openComposer(null)} />

      {/* Composer panel */}
      <ComposerPanel
        open={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setEditingArticle(null);
        }}
        onSaved={onSaved}
        article={editingArticle}
      />
    </div>
  );
}

const KPI = ({ label, v, accent, testid }) => (
  <div className="bg-ink p-5" data-testid={testid}>
    <div className="label-mono text-bone/50">{label}</div>
    <div className={`font-display text-3xl font-black mt-1 ${accent ? "text-safety" : "text-bone"}`}>
      {v}
    </div>
  </div>
);
